import { memo } from 'react';
import { SxProps } from '@mui/material';
import { FieldProps, useRecordContext } from 'react-admin';
import { format } from 'date-fns';

interface Props extends FieldProps<any> {
    size?: string;
    sx?: SxProps;
}

const multiLevelObjectToSingleLevelObject = (obj: any) => {
    const _obj: any = {};
    Object.keys(obj || {}).forEach(key => {
        if (obj[key] && typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
            Object.keys(obj[key]).forEach(_key => {
                _obj[`${key}.${_key}`] = obj[key][_key];
            });
        } else {
            _obj[key] = obj[key];
        }
    });
    return _obj;
};

const FormattedDateTimeField = (props: Props) => {
    const record = useRecordContext<any>();
    if (!record) return null;

    const created = record.created_at || record.createdAt;
    const updated = record.updated_at || record.updatedAt;
    const startTime = record.start_time || record.startTime;
    const endTime = record.end_time || record.endTime;

    const isCreatedSource = props.source === 'created_at' || props.source === 'createdAt';
    const isStartTimeSource = props.source === 'start_time' || props.source === 'startTime';

    if (!isCreatedSource && !isStartTimeSource) {
        const _re: any = multiLevelObjectToSingleLevelObject(record);
        const value = _re[props.source || ''];
        return value ? (
            <div className="flex flex-col text-sm space-y-1">
                <div className="flex items-center space-x-2">
                    <div className="whitespace-nowrap">
                        {format(new Date(value), 'dd-MMM-yyyy hh:mm a')}
                    </div>
                </div>
            </div>
        ) : null;
    }

    if (isCreatedSource && created) {
        return (
            <div className="flex flex-col text-center text-sm space-y-1">
                <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 flex flex-col items-center rounded-full bg-amber-200">
                        C
                    </div>
                    <div className="whitespace-nowrap">
                        {format(new Date(created), 'dd-MMM-yyyy hh:mm a')}
                    </div>
                </div>
                {updated && (
                    <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 flex flex-col items-center rounded-full bg-pink-200">
                            U
                        </div>
                        <div className="whitespace-nowrap">
                            {format(new Date(updated), 'dd-MMM-yyyy hh:mm a')}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    if (isStartTimeSource && startTime) {
        return (
            <div className="flex flex-col text-center text-sm space-y-1">
                <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 flex flex-col items-center rounded-full bg-green-200">
                        S
                    </div>
                    <div className="whitespace-nowrap">
                        {format(new Date(startTime), 'dd-MMM-yyyy hh:mm a')}
                    </div>
                </div>
                {endTime && (
                    <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 flex flex-col items-center rounded-full bg-red-300">
                            E
                        </div>
                        <div className="whitespace-nowrap">
                            {format(new Date(endTime), 'dd-MMM-yyyy hh:mm a')}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return null;
};

FormattedDateTimeField.defaultProps = {
    source: 'created_at',
    label: 'Created - Updated',
};

export default memo<Props>(FormattedDateTimeField);
